import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { 
  Building2, CheckCircle, Loader2, ExternalLink, AlertCircle, 
  DollarSign, ArrowDownToLine, Lock
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

export function ConnectBankModal({ open, onClose }) {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    if (open) fetchStatus();
  }, [open]);

  const fetchStatus = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/connect/status`, { withCredentials: true });
      setStatus(res.data);
    } catch {} 
    finally { setLoading(false); }
  };

  const handleConnect = async () => {
    setConnecting(true);
    try {
      const res = await axios.post(`${API}/api/connect/create-account`, {
        origin_url: window.location.origin
      }, { withCredentials: true });
      
      if (res.data.status === 'already_connected') {
        toast.success('Bank account already connected!');
        fetchStatus();
      } else if (res.data.url) {
        window.location.href = res.data.url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start connection');
    } finally {
      setConnecting(false);
    }
  };

  const isConnected = status?.connected === true;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="connect-bank-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-white flex items-center gap-3">
            <Building2 className="h-6 w-6 text-[#9333EA]" />
            {isConnected ? 'Bank Connected' : 'Connect Your Bank'}
          </DialogTitle>
          <DialogDescription className="text-[#A19DB0]">
            {isConnected 
              ? 'Your bank account is connected. Deposits go directly to your account.'
              : 'Connect your bank to deposit and withdraw real money securely.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-6">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-[#9333EA]" />
            </div>
          ) : isConnected ? (
            <>
              <div className="p-4 bg-emerald-500/10 border border-emerald-500/20 rounded-md flex items-center gap-3">
                <CheckCircle className="h-6 w-6 text-emerald-400 shrink-0" />
                <div>
                  <p className="text-white font-semibold">Bank Account Connected</p>
                  <p className="text-sm text-[#A19DB0]">
                    Payouts: {status.payouts_enabled ? 'Enabled' : 'Pending activation'}
                  </p>
                </div>
              </div>

              <div className="space-y-3">
                <div className="p-4 bg-[#12121A] rounded-md border border-white/10">
                  <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0] mb-2">How It Works</div>
                  <ul className="space-y-2 text-sm text-[#A19DB0]">
                    <li className="flex items-start gap-2">
                      <DollarSign className="h-4 w-4 text-[#9333EA] mt-0.5 shrink-0" />
                      Card deposits go directly to your connected account
                    </li>
                    <li className="flex items-start gap-2">
                      <Lock className="h-4 w-4 text-[#9333EA] mt-0.5 shrink-0" />
                      Funds stay locked until the goal's unlock date
                    </li>
                    <li className="flex items-start gap-2">
                      <ArrowDownToLine className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                      On unlock, withdraw to your bank instantly
                    </li>
                  </ul>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="p-4 bg-[#12121A] rounded-md border border-white/10">
                <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0] mb-3">Why Connect?</div>
                <ul className="space-y-3 text-sm text-[#A19DB0]">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    Your deposits go to YOUR bank account, not the platform
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    Money is locked until your chosen unlock date
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                    Withdraw to your bank when goals unlock
                  </li>
                  <li className="flex items-start gap-2">
                    <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
                    A 2% service fee applies to all deposits
                  </li>
                </ul>
              </div>

              <div className="p-3 bg-[#9333EA]/10 border border-[#9333EA]/20 rounded-md">
                <p className="text-xs text-[#A19DB0]">
                  You'll be redirected to Stripe to securely connect your bank account. FutureVault never sees your banking details.
                </p>
              </div>

              <Button
                data-testid="connect-bank-button"
                onClick={handleConnect}
                disabled={connecting}
                className="w-full h-12 btn-primary font-semibold"
              >
                {connecting ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Building2 className="h-5 w-5 mr-2" />
                    Connect Bank Account
                    <ExternalLink className="h-4 w-4 ml-2" />
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function WithdrawToBankModal({ open, onClose, goal, onSuccess }) {
  const [amount, setAmount] = useState('');
  const [loading, setLoading] = useState(false);
  const [balance, setBalance] = useState(null);

  useEffect(() => {
    if (open) fetchBalance();
  }, [open]);

  const fetchBalance = async () => {
    try {
      const res = await axios.get(`${API}/api/connect/balance`, { withCredentials: true });
      setBalance(res.data);
    } catch {}
  };

  const handleWithdraw = async () => {
    if (!amount || parseFloat(amount) <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/api/connect/withdraw`, {
        goal_id: goal.goal_id,
        amount: parseFloat(amount)
      }, { withCredentials: true });
      toast.success(res.data.message);
      onSuccess();
      onClose();
      setAmount('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Withdrawal failed');
    } finally {
      setLoading(false);
    }
  };

  if (!goal) return null;
  const isUnlocked = new Date(goal.unlock_date) <= new Date();

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="glass-modal sm:max-w-md" data-testid="withdraw-bank-modal">
        <DialogHeader>
          <DialogTitle className="font-heading text-xl font-bold text-white flex items-center gap-3">
            <ArrowDownToLine className="h-6 w-6 text-emerald-400" />
            Withdraw to Bank
          </DialogTitle>
          <DialogDescription className="text-[#A19DB0]">
            from "{goal.name}"
          </DialogDescription>
        </DialogHeader>

        <div className="mt-6 space-y-5">
          {!isUnlocked ? (
            <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-md flex items-center gap-3">
              <Lock className="h-6 w-6 text-amber-400" />
              <div>
                <p className="text-white font-semibold">Goal Still Locked</p>
                <p className="text-sm text-[#A19DB0]">
                  Unlocks on {new Date(goal.unlock_date).toLocaleDateString()}
                </p>
              </div>
            </div>
          ) : (
            <>
              {balance && (
                <div className="grid grid-cols-2 gap-3">
                  <div className="p-3 bg-[#12121A] rounded-md border border-white/10">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0] mb-1">Available</div>
                    <div className="font-mono text-lg font-bold text-emerald-400">${balance.available?.toFixed(2) || '0.00'}</div>
                  </div>
                  <div className="p-3 bg-[#12121A] rounded-md border border-white/10">
                    <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0] mb-1">Pending</div>
                    <div className="font-mono text-lg font-bold text-amber-400">${balance.pending?.toFixed(2) || '0.00'}</div>
                  </div>
                </div>
              )}

              <div className="space-y-2">
                <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#A19DB0]">
                  Amount to Withdraw
                </Label>
                <div className="relative">
                  <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#A19DB0]" />
                  <Input
                    data-testid="bank-withdraw-amount-input"
                    type="number"
                    min="0.01"
                    step="0.01"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    className="pl-10 input-dark"
                    placeholder="0.00"
                  />
                </div>
              </div>

              <div className="p-3 bg-[#12121A] rounded-md border border-white/10">
                <p className="text-xs text-[#A19DB0]">
                  Funds will be sent to your connected bank account. Payouts typically arrive in 1-2 business days.
                </p>
              </div>

              <Button
                data-testid="submit-bank-withdraw-button"
                onClick={handleWithdraw}
                disabled={loading}
                className="w-full h-12 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold transition-colors"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <ArrowDownToLine className="h-5 w-5 mr-2" />
                    Withdraw to Bank
                  </>
                )}
              </Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
